import Dexie, { type EntityTable } from "dexie";
import type {
  Family,
  Invite,
  Item,
  List,
  MealPlanEntry,
  Member,
  OutboxEntry,
  PantryItem,
  PartnerConnection,
  PriceEntry,
  Product,
  Recipe,
  RecipeIngredient,
  Store,
  SyncCursor,
  Trip,
  TripItem,
} from "@ainkauf/core";

/**
 * Der lokale Speicher. Die Oberfläche liest ausschließlich hier (Regel 1),
 * nie direkt bei Supabase. Der Sync füllt diese Tabellen und leert die Outbox.
 *
 * IndexedDB kann keine Booleans und keine Nullwerte indizieren. `is_checked`
 * und `deleted_at` stehen deshalb bewusst in keinem Index, sie werden nach dem
 * Laden gefiltert. Bei Familienlisten sind das Dutzende Zeilen, kein Problem.
 */
export class AInkaufDb extends Dexie {
  families!: EntityTable<Family, "id">;
  members!: EntityTable<Member, "id">;
  invites!: EntityTable<Invite, "id">;
  stores!: EntityTable<Store, "id">;
  lists!: EntityTable<List, "id">;
  items!: EntityTable<Item, "id">;
  products!: EntityTable<Product, "id">;
  trips!: EntityTable<Trip, "id">;
  trip_items!: EntityTable<TripItem, "id">;
  price_entries!: EntityTable<PriceEntry, "id">;
  recipes!: EntityTable<Recipe, "id">;
  recipe_ingredients!: EntityTable<RecipeIngredient, "id">;
  pantry_items!: EntityTable<PantryItem, "id">;
  meal_plan_entries!: EntityTable<MealPlanEntry, "id">;
  partner_connections!: EntityTable<PartnerConnection, "id">;
  outbox!: EntityTable<OutboxEntry, "id">;
  cursors!: EntityTable<SyncCursor, "table">;
  photo_queue!: EntityTable<PhotoQueueEntry, "id">;

  constructor(name = "ainkaufsagent") {
    super(name);
    this.version(1).stores({
      members: "id, family_id, user_id, updated_at",
      stores: "id, family_id, updated_at",
      lists: "id, family_id, sort_index, updated_at",
      items: "id, list_id, family_id, normalized_name, sort_index, updated_at",
      products: "id, family_id, normalized_name, use_count, updated_at",
      trips: "id, family_id, started_at, updated_at",
      trip_items: "id, trip_id, family_id, updated_at",
      outbox: "id, table, row_id, next_attempt_at, client_updated_at",
      cursors: "table",
    });

    // Fassung 2: Einladungen. Geräte, die schon Fassung 1 haben, bekommen die
    // Tabelle beim nächsten Start dazu, ohne dass etwas verloren geht.
    this.version(2).stores({
      invites: "id, family_id, code, updated_at",
    });

    // Fassung 3: die Familie selbst, wegen des Monatsbudgets. Sie wird nur
    // gelesen und über die Operation "update" geändert, nie angelegt.
    this.version(3).stores({
      families: "id, updated_at",
    });

    // Fassung 4: Bilder, die auf ihren Upload warten.
    this.version(4).stores({
      photo_queue: "id, item_id, family_id, created_at",
    });

    // Fassung 5: Bilder liegen als Bytes statt als Blob. Alte Einträge werden
    // verworfen, sie ließen sich auf iOS ohnehin nie anlegen.
    this.version(5)
      .stores({ photo_queue: "id, item_id, family_id, created_at" })
      .upgrade(async (tx) => {
        await tx.table("photo_queue").clear();
      });

    // Fassung 6: beobachtete Preise. Eine Zeile je Bonposition, nie
    // überschrieben, deshalb der Index auf Produkt und Zeitpunkt.
    this.version(6).stores({
      price_entries: "id, family_id, product_id, observed_at, updated_at",
    });

    // Fassung 7: Rezepte, Zutaten, Vorräte und Wochenplan (Stufe 5). Der
    // Wochenplan wird immer für eine Woche gelesen, deshalb der Index auf
    // den Tag.
    this.version(7).stores({
      recipes: "id, family_id, normalized_name, updated_at",
      recipe_ingredients: "id, family_id, recipe_id, updated_at",
      pantry_items: "id, family_id, normalized_name, product_id, updated_at",
      meal_plan_entries: "id, family_id, day, recipe_id, updated_at",
    });

    // Fassung 8: die Verbindung zur Spaisekammer (Stufe S2). Sie wird nur
    // gelesen, der Pull füllt sie; die Outbox bleibt leer, weil das Depot nie
    // writeWithOutbox darauf ruft.
    this.version(8).stores({
      partner_connections: "id, family_id, provider, updated_at",
    });
  }
}

/**
 * Ein Bild, das noch hochgeladen werden muss. Bilder gehen nicht durch die
 * normale Outbox: dort liegen Tabellenzeilen, hier liegen Megabyte.
 *
 * Gespeichert werden **Bytes, kein Blob**. Safari und alle iOS-Browser können
 * Blobs nicht in IndexedDB ablegen und melden nur "Error preparing Blob/File
 * data to be stored in object store". Auf dem iPhone wäre jedes Foto
 * stillschweigend verschwunden.
 */
export interface PhotoQueueEntry {
  id: string;
  item_id: string;
  family_id: string;
  bytes: ArrayBuffer;
  mime: string;
  created_at: string;
  attempts: number;
  last_error: string | null;
}

/** Baut aus den gespeicherten Bytes wieder ein Bild. */
export function alsBlob(eintrag: PhotoQueueEntry): Blob {
  return new Blob([eintrag.bytes], { type: eintrag.mime });
}

export const db = new AInkaufDb();

/** UUIDs kommen vom Client (Regel 2), damit Offline-Anlagen sofort eine Identität haben. */
export function newId(): string {
  return crypto.randomUUID();
}

export function nowIso(): string {
  return new Date().toISOString();
}
